/**
 * SOPPage — Statement of Purpose generator.
 *
 * Flow:
 *   1. On mount: check for existing SOP in /api/documents?type=SOP
 *   2. If found: jump straight to editor view
 *   3. If not: show setup form → Generate → stream into editor → save
 */

import { useState, useEffect, useRef } from 'react'
import { marked } from 'marked'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../lib/supabase'
import api from '../../lib/api'
import DocumentEditor from '../../components/documents/DocumentEditor'
import { Sparkles, ArrowLeft, ChevronRight, Loader2 } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

// ─── Word-count segmented control ─────────────────────────────────────────────

const WORD_OPTIONS = [500, 600, 650, 700, 800]

function WordControl({ value, onChange }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {WORD_OPTIONS.map(w => (
        <button
          key={w}
          type="button"
          onClick={() => onChange(w)}
          className="px-3 py-1.5 rounded-lg font-mono text-xs font-medium transition-all"
          style={{
            background: value === w ? 'rgba(37,99,235,0.15)' : '#F4F7FC',
            border: `1px solid ${value === w ? 'rgba(37,99,235,0.5)' : '#DDE4EF'}`,
            color: value === w ? '#2563eb' : '#506280',
          }}
        >
          {w}
        </button>
      ))}
    </div>
  )
}

// ─── Form field ────────────────────────────────────────────────────────────────

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block font-sans text-sm font-medium text-acdyon-textMuted mb-1.5">
        {label}
        {required && <span style={{ color: '#2563eb' }}> *</span>}
      </label>
      {hint && <p className="font-sans text-xs text-acdyon-textFaint mb-2">{hint}</p>}
      {children}
    </div>
  )
}

const inputCls = "w-full px-3.5 py-2.5 rounded-lg font-sans text-sm text-acdyon-text placeholder:text-acdyon-textFaint outline-none transition-colors"
const inputStyle = { background: '#FFFFFF', border: '1px solid #C8D4E4' }
const inputFocus = {
  onFocus: e => (e.currentTarget.style.borderColor = '#2563eb'),
  onBlur:  e => (e.currentTarget.style.borderColor = '#C8D4E4'),
}

// ─── Existing SOP card ────────────────────────────────────────────────────────

function ExistingCard({ doc, onEdit, onNew }) {
  return (
    <div
      className="rounded-2xl p-6 space-y-4"
      style={{ background: '#FFFFFF', border: '1px solid #DDE4EF', maxWidth: 520 }}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.2)' }}>
          <Sparkles style={{ width: 18, height: 18, color: '#2563eb' }} />
        </div>
        <div>
          <p className="font-sans font-semibold text-acdyon-text text-sm">{doc.title}</p>
          <p className="font-mono text-xs text-acdyon-textFaint mt-0.5">
            {(doc.word_count || 0).toLocaleString()} words · {new Date(doc.updated_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className="flex gap-3">
        <button
          onClick={onEdit}
          className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
          style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.3)', color: '#2563eb' }}
        >
          Continue Editing
        </button>
        <button
          onClick={onNew}
          className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
          style={{ background: '#FFFFFF', border: '1px solid #C8D4E4', color: '#506280' }}
        >
          Generate New
        </button>
      </div>
    </div>
  )
}

// ─── Setup Form ────────────────────────────────────────────────────────────────

function SetupForm({ profile, onGenerate, loading }) {
  const [form, setForm] = useState({
    university:             profile?.university || '',
    programme:              profile?.programme  || '',
    professionalBackground: '',
    motivation:             '',
    researchInterest:       profile?.research_topic || '',
    targetWords:            650,
  })

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }))

  const canSubmit = form.university && form.programme && form.professionalBackground && form.motivation

  return (
    <form
      onSubmit={e => { e.preventDefault(); if (canSubmit) onGenerate(form) }}
      className="space-y-6"
    >
      {/* Row 1: University + Programme */}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Target University" required>
          <input
            className={inputCls} style={inputStyle} {...inputFocus}
            value={form.university}
            onChange={set('university')}
            placeholder="e.g. University of Cambridge"
          />
        </Field>
        <Field label="Programme" required>
          <input
            className={inputCls} style={inputStyle} {...inputFocus}
            value={form.programme}
            onChange={set('programme')}
            placeholder="e.g. PhD Computer Science"
          />
        </Field>
      </div>

      {/* Professional Background */}
      <Field
        label="Professional Background"
        hint="Briefly describe your work experience, degrees, and any notable achievements."
        required
      >
        <textarea
          className={inputCls} style={inputStyle} {...inputFocus}
          rows={4}
          value={form.professionalBackground}
          onChange={set('professionalBackground')}
          placeholder="e.g. 3 years as a software engineer at Infosys, B.Tech in CS from IIT Delhi with distinction…"
        />
      </Field>

      {/* Motivation */}
      <Field
        label="Motivation"
        hint="What specific moment, project, or realisation drove you to pursue this programme?"
        required
      >
        <textarea
          className={inputCls} style={inputStyle} {...inputFocus}
          rows={4}
          value={form.motivation}
          onChange={set('motivation')}
          placeholder="e.g. While debugging a production outage I realised that our monitoring tools were fundamentally limited by…"
        />
      </Field>

      {/* Research Interest */}
      <Field
        label="Research Interest"
        hint="What research question or area do you want to investigate during your PhD?"
      >
        <input
          className={inputCls} style={inputStyle} {...inputFocus}
          value={form.researchInterest}
          onChange={set('researchInterest')}
          placeholder="e.g. Explainability in large language models"
        />
      </Field>

      {/* Target words */}
      <Field label="Target Length">
        <WordControl value={form.targetWords} onChange={v => setForm(prev => ({ ...prev, targetWords: v }))} />
      </Field>

      <button
        type="submit"
        disabled={!canSubmit || loading}
        className="flex items-center gap-2 px-6 py-3 rounded-xl font-sans text-sm font-semibold transition-all disabled:opacity-50"
        style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.4)', color: '#2563eb' }}
      >
        {loading ? <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> : <Sparkles style={{ width: 15, height: 15 }} />}
        Generate My SOP
        <ChevronRight style={{ width: 15, height: 15 }} />
      </button>
    </form>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SOPPage() {
  const { profile } = useAuthStore()

  const [phase,       setPhase]       = useState('loading')  // loading | check | form | editor
  const [existingDoc, setExistingDoc] = useState(null)
  const [documentId,  setDocumentId]  = useState(null)
  const [docTitle,    setDocTitle]    = useState('')
  const [docContent,  setDocContent]  = useState('')
  const [streaming,   setStreaming]   = useState(false)
  const [formData,    setFormData]    = useState(null)

  const editorRef = useRef(null)
  const abortRef  = useRef(null)

  // ── Check for existing SOP ──────────────────────────────────────────────

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get('/documents')
        const sop = data.find(d => d.type === 'SOP')
        if (sop) {
          setExistingDoc(sop)
          setPhase('check')
        } else {
          setPhase('form')
        }
      } catch {
        setPhase('form')
      }
    }
    check()
  }, [])

  // ── Open existing doc in editor ─────────────────────────────────────────

  const openExisting = () => {
    setDocumentId(existingDoc.id)
    setDocTitle(existingDoc.title)
    setDocContent(existingDoc.content || '')
    setPhase('editor')
  }

  // ── Generate (SSE stream) ───────────────────────────────────────────────

  const handleGenerate = async (params) => {
    setFormData(params)
    setPhase('editor')
    setStreaming(true)

    // Give React a tick to mount the editor
    await new Promise(r => setTimeout(r, 120))
    editorRef.current?.clearContent()

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token

      const resp = await fetch(`${API_BASE}/documents/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ type: 'SOP', params }),
      })

      const reader  = resp.body.getReader()
      const decoder = new TextDecoder()
      abortRef.current = reader
      let buffer     = ''
      let fullMarkdown = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()
          if (payload === '[DONE]') continue
          try {
            const evt = JSON.parse(payload)
            if (evt.text) {
              fullMarkdown += evt.text
            } else if (evt.documentId) {
              setDocumentId(evt.documentId)
              setDocTitle(evt.title || 'Statement of Purpose')
            } else if (evt.error) {
              throw new Error(evt.error)
            }
          } catch (e) {
            if (e?.message && !e.message.includes('JSON')) throw e
          }
        }
      }

      // Convert full markdown → HTML and set editor content
      if (fullMarkdown.trim()) {
        const html = await marked.parse(fullMarkdown, { async: false })
        editorRef.current?.setHTML(html)
      }
    } catch (err) {
      console.error('SOP generation error:', err)
    } finally {
      setStreaming(false)
    }
  }

  const stopStreaming = () => {
    abortRef.current?.cancel?.()
    setStreaming(false)
  }

  // ── Render ──────────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 4rem)' }}>
        <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  if (phase === 'editor') {
    return (
      <DocumentEditor
        ref={editorRef}
        documentId={documentId}
        initialContent={docContent}
        placeholder="Your Statement of Purpose begins here…"
        targetWords={formData?.targetWords || existingDoc?.word_count || 650}
        docTitle={docTitle}
        streaming={streaming}
        streamingLabel="Writing your Statement of Purpose…"
        onStopStreaming={streaming ? stopStreaming : null}
        onSaved={(id) => setDocumentId(id)}
      />
    )
  }

  return (
    <div className="min-h-screen p-8" style={{ background: '#F4F7FC' }}>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.2)' }}>
              <Sparkles style={{ width: 18, height: 18, color: '#2563eb' }} />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-acdyon-text">Statement of Purpose</h1>
              <p className="font-sans text-sm text-acdyon-textFaint">AI-drafted, first-person, no buzzwords</p>
            </div>
          </div>
        </div>

        {/* Existing SOP check */}
        {phase === 'check' && existingDoc && (
          <div className="mb-8">
            <p className="font-sans text-sm text-acdyon-textMuted mb-4">
              You have an existing SOP. Would you like to continue editing it or generate a fresh one?
            </p>
            <ExistingCard
              doc={existingDoc}
              onEdit={openExisting}
              onNew={() => setPhase('form')}
            />
          </div>
        )}

        {/* Setup form */}
        {phase === 'form' && (
          <>
            {phase === 'form' && existingDoc && (
              <button
                onClick={() => setPhase('check')}
                className="flex items-center gap-1.5 mb-6 font-sans text-sm text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              >
                <ArrowLeft style={{ width: 14, height: 14 }} />
                Back to existing SOP
              </button>
            )}

            <div
              className="rounded-2xl p-6"
              style={{ background: '#FFFFFF', border: '1px solid #DDE4EF' }}
            >
              <h2 className="font-sans text-sm font-semibold text-acdyon-text mb-1">About You</h2>
              <p className="font-sans text-xs text-acdyon-textFaint mb-6">
                Tell us about your background and motivation — the AI will craft your unique story.
              </p>
              <SetupForm profile={profile} onGenerate={handleGenerate} loading={false} />
            </div>
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
